import { defineStore } from 'pinia'
import client from '@/api/client'
import { FALLBACK_SCHOOL_NAME, FALLBACK_LOGO, FALLBACK_CURRENCY } from '@/config/branding'
import { CURRENCIES } from '@/config/currencies'
import { isPlatformHost } from '@/config/host'

// A login background at or below this size (px, either dimension) tiles;
// anything larger covers like a full-bleed photo.
export const BG_TILE_THRESHOLD = 600

export const useBrandingStore = defineStore('branding', {
  state: () => ({
    schoolName: null, // set by super admin in Settings
    logoUrl: null, // set by super admin in Settings
    address: null,
    phone: null,
    email: null,
    website: null,
    primaryColor: null,
    secondaryColor: null,
    accentColor: null,
    currencyCode: null, // set by super admin in Settings
    loginBackgroundUrl: null, // set by super admin in Settings (Appearance)
    loaded: false,
  }),

  getters: {
    // Always resolve to something renderable, falling back to the bundled defaults.
    name: (state) => state.schoolName || FALLBACK_SCHOOL_NAME,
    logo: (state) => state.logoUrl || FALLBACK_LOGO,
    // Resolve the configured currency code to its symbol/locale, else fall back.
    currency: (state) =>
      CURRENCIES.find((c) => c.code === state.currencyCode) || FALLBACK_CURRENCY,
  },

  actions: {
    async load(force = false) {
      if (this.loaded && !force) return
      // On the platform apex (SaaS product surfaces: marketing site, platform
      // console, apex login/signup) the theme is the platform admin's palette.
      // Individual school interfaces and their student/parent portals live on a
      // tenant subdomain and keep their own colours (styles.css as the fallback),
      // so they must never inherit the platform palette.
      if (isPlatformHost()) {
        await this.loadPlatform()
        return
      }
      try {
        const { data } = await client.get('/settings/public')
        this.schoolName = data.school_name || null
        this.logoUrl = data.logo_url || null
        this.address = data.address || null
        this.phone = data.phone || null
        this.email = data.email || null
        this.website = data.website || null
        this.primaryColor = data.primary_color || null
        this.secondaryColor = data.secondary_color || null
        this.accentColor = data.accent_color || null
        this.currencyCode = data.currency_code || null
        this.loginBackgroundUrl = data.login_background_url || null
        this.applyTheme()
      } catch (e) {
        // Branding is non-critical; fall back to the bundled defaults.
      } finally {
        this.loaded = true
      }
    },

    // Apex/platform surfaces theme from the platform admin's branding, not any
    // tenant's. Blank colours fall through to the styles.css :root defaults.
    async loadPlatform() {
      try {
        const { data } = await client.get('/platform/branding')
        const b = data.data || {}
        this.schoolName = b.name || null
        this.logoUrl = b.logo || null
        this.primaryColor = b.primary_color || null
        this.secondaryColor = b.secondary_color || null
        this.accentColor = b.accent_color || null
        this.loginBackgroundUrl = b.login_background_url || null
        this.applyTheme()
      } catch (e) {
        // Branding is non-critical; fall back to the bundled defaults.
      } finally {
        this.loaded = true
      }
    },

    // Push the configured palette onto :root as inline CSS variables.
    applyTheme() {
      applyThemeVars({
        primaryColor: this.primaryColor,
        secondaryColor: this.secondaryColor,
        accentColor: this.accentColor,
        loginBackgroundUrl: this.loginBackgroundUrl,
      })
    },
  },
})

// Push a palette + login background onto :root as inline CSS variables. Inline
// custom properties win over the stylesheet's :root, so any colour left blank
// simply falls back to the value in styles.css. Derived shades (rgb + darker
// primary) keep Bootstrap components and hover states in sync. Shared by the
// tenant branding store and the apex platform login (both drive .auth-shell).
export function applyThemeVars({ primaryColor, secondaryColor, accentColor, loginBackgroundUrl } = {}) {
  const root = document.documentElement
  const set = (name, val) => val && root.style.setProperty(name, val)

  set('--brand-primary', primaryColor)
  set('--brand-secondary', secondaryColor)
  set('--brand-accent', accentColor)

  if (primaryColor) {
    const rgb = hexToRgb(primaryColor)
    if (rgb) root.style.setProperty('--bs-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`)
    set('--brand-primary-dark', darken(primaryColor, 0.18))
  }

  // Login/auth background image. Left unset when blank so the CSS var() fallback
  // keeps the bundled photo. Encoded as a url() so it drops straight into the
  // .auth-shell background-image.
  if (loginBackgroundUrl) {
    root.style.setProperty('--auth-bg', `url("${loginBackgroundUrl}")`)
    // Small images tile to fill the screen; large ones cover like a photo.
    // Natural size is only known once the image loads, so decide async.
    const img = new Image()
    img.onload = () => {
      const small = img.naturalWidth <= BG_TILE_THRESHOLD && img.naturalHeight <= BG_TILE_THRESHOLD
      root.style.setProperty('--auth-bg-size', small ? 'auto' : 'cover')
      root.style.setProperty('--auth-bg-repeat', small ? 'repeat' : 'no-repeat')
    }
    img.src = loginBackgroundUrl
  } else {
    root.style.removeProperty('--auth-bg')
    root.style.removeProperty('--auth-bg-size')
    root.style.removeProperty('--auth-bg-repeat')
  }
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((hex || '').trim())
  return m
    ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
    : null
}

function darken(hex, amount) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const f = Math.max(0, 1 - amount)
  const c = (v) => Math.round(v * f).toString(16).padStart(2, '0')
  return `#${c(rgb.r)}${c(rgb.g)}${c(rgb.b)}`
}
